import type { ThemedBaseButton } from '@common/types/base-button-widget.type';

import {
    EButtonStyleType,
    EButtonType,
    EButtonWidthType,
    EHorizontalPosition,
    ESize,
    FormStyleEnum,
    InputBorderEnum,
} from '@common/enums';
import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';

import type { CssVars } from '../../../css-vars';
import { shadowValueVar } from '../../../features/shadow/shadow.css-vars';
import { pxVar } from '../../../mixins/number-with-unit/number-with-unit.css-vars';
import { rotateVar } from '../../../mixins/rotation/rotation.css-vars';

import type { ColorCssResolver } from './themed-button.css-vars';
import { getThemedButtonCssVars, getThemedButtonHoverClasses } from './themed-button.css-vars';

/**
 * Host types this module reads structurally. `FormInputSettings` /
 * `FormButtonSettings` live in `libs/components`, and the widget data type in the
 * websites app — both Angular-side, while entity-core stays framework-free — so
 * they are mirrored here as the shapes these builders actually touch. The host's
 * concrete types satisfy them without a cast.
 */
export type FormInputSettingsLike = Record<string, any>;
export type FormButtonSettingsLike = ThemedBaseButton & Record<string, any>;
export type ChatBotsButtonSettingsLike = Record<string, any>;
export type FormWidgetLike = { type?: string; data: Record<string, any> };

/**
 * Theme values the root builder bakes in, resolved by the host: entity-core
 * cannot reach `@sites/site-theme` (an Angular library), so it takes the two computed
 * strings rather than the resolver plus four theme enums.
 */
export type FormRootThemeTokens = {
    /** `themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily)` */
    fontFamily: string;
    /** `themeColorCss(ThemeColorNames.primary)` */
    dividerColor: string;
};

/**
 * CSS custom properties for the FORM widget — the LESS twin of the former JSS
 * `getBaseFormStyles` (Widgets.FORM slice). ONE module shared by both apps; the
 * static rules consuming the variables live in each surface's `.less`
 * (editor `form-view.component.less` / builder `form.component.less`, plus the
 * modal twins in `form-modal-preview` / `form-modal`).
 *
 * The shared `getBaseFormStyles` is intentionally NOT touched — it still serves
 * the payment-form, login-edu and shop-cart widgets (all still on JSS). The form
 * forks its own slice here.
 *
 * Variables cascade: the inline-form input/text/radius variables are set on the
 * root element and inherited by the field/control descendants, so the template
 * only binds them once. A `null` value removes the property (mirroring the
 * omitted-key behaviour of the old JSS object); per-device overrides use
 * `var(--x-mobile, var(--x))` fallback chains in LESS.
 *
 * Discrete S/M/L size metrics are CSS variables too — the size→px lookups are
 * owned by this module (the size-token tables below), set as cascading vars on
 * the element that carries each size source: input size on `.form-root`, button
 * size on `.form-button-wrapper`, chat-bot size on `.form-chatbot-wrapper`. The
 * vertical/horizontal + alignment enums stay as modifier classes. Colours, radii,
 * shadow strings, rotation and the custom-button bag are variables too. Every
 * variable-driven themed rule is gated behind a modifier class so an unset
 * property never clobbers theme/global styling.
 */
export type FormCssVars = CssVars;

/**
 * Horizontal-alignment values (driven by `inputSettings.horizontalPosition`).
 * Alignment maps to two CSS properties depending on layout context: `margin`
 * (vertical root + button) and `justify-content` (horizontal root/fields +
 * chat-bot row). Both are set as cascading vars on the form root.
 */
type FormAlignTokens = {
    margin: string;
    justify: string;
};

const FORM_ALIGNMENTS: Record<EHorizontalPosition, FormAlignTokens> = {
    [EHorizontalPosition.L]: { margin: '0 auto 0 0', justify: 'flex-start' },
    [EHorizontalPosition.M]: { margin: '0 auto', justify: 'center' },
    [EHorizontalPosition.R]: { margin: '0 0 0 auto', justify: 'flex-end' },
};

// ---------------------------------------------------------------------------
// Root (the `form` + `formPosition` JSS keys, merged onto the form root div)
// ---------------------------------------------------------------------------

export function getFormRootCssVars(widget: FormWidgetLike, theme: FormRootThemeTokens): FormCssVars {
    const isZero = widget.type === 'zero';
    const position = widget.data?.position;

    return {
        '--form-font-family': theme.fontFamily,
        '--form-divider-bg': theme.dividerColor,
        '--form-transform': isZero ? 'none' : rotateVar(position?.rotate),
        // Tablet falls back to the mobile rotation when unset (matches the text widget).
        '--form-transform-tablet': isZero ? null : rotateVar(position?.tabletRotate ?? position?.mobileRotate),
        '--form-transform-mobile': isZero ? null : rotateVar(position?.mobileRotate),
    };
}

/** `form-vertical|form-horizontal` layout class (alignment is variable-driven). */
export function getFormRootCssClasses(inputSettings: FormInputSettingsLike): string {
    return inputSettings.formStyleType === FormStyleEnum.HORIZONTAL ? 'form-horizontal' : 'form-vertical';
}

/** Horizontal-alignment variables (cascade to the button + chat-bot row). */
export function getFormAlignCssVars(inputSettings: FormInputSettingsLike): FormCssVars {
    const align = FORM_ALIGNMENTS[inputSettings.horizontalPosition ?? EHorizontalPosition.L];

    return {
        '--form-align-margin': align.margin,
        '--form-align-justify': align.justify,
    };
}

// ---------------------------------------------------------------------------
// Input theming (the `formControl` / `formControlTextarea` / `formField` keys).
// Set on the root; cascades to every field/control descendant.
// ---------------------------------------------------------------------------

export function getFormInputCssVars(inputSettings: FormInputSettingsLike, resolveColor: ColorCssResolver): FormCssVars {
    return {
        '--form-text-color': resolveColor(inputSettings.textColor),
        '--form-input-bg': resolveColor(inputSettings.backgroundColor),
        '--form-input-border-color': resolveColor(inputSettings.borderColor),
        '--form-input-text-color': resolveColor(inputSettings.inputTextColor),
        '--form-input-radius': pxVar(inputSettings.borderRadius),
    };
}

/** Full/bottom border gate, shared by every field and control (sizing is variable-driven). */
export function getFormInputCssClasses(inputSettings: FormInputSettingsLike): string {
    return inputSettings.inputBorderType === InputBorderEnum.BOTTOM_BORDER
        ? 'form-input-bottom-border'
        : 'form-input-full-border';
}

// ---------------------------------------------------------------------------
// Per-size metrics — the former `&.form-size-S|M|L` LESS blocks. This module
// owns the fixed size→px values; the static LESS consumes the variables once.
// `null` size falls back to the M preset everywhere.
// ---------------------------------------------------------------------------

/** `n` → `"npx"`. */
/** `[v, h]` → the `"Vpx Hpx"` padding shorthand. */
const padding = ([vertical, horizontal]: [number, number]): string => `${vertical}px ${horizontal}px`;

/**
 * Input-size metrics (driven by `inputSettings.inputSize`). Covers the field
 * label/paragraph/checkbox-radio typography, the vertical field gap and the
 * shared `.form-control` / `.form-control-textarea` box. The horizontal field's
 * single-line textarea reuses `--form-control-height` (same value, same source).
 * Set on `.form-root`, inherited by every field/control descendant.
 */
type FormInputSizeTokens = {
    labelFontSize: number;
    labelMarginBottom: number;
    paragraphFontSize: number;
    checkboxRadioSize: number;
    fieldMarginBottom: number;
    controlHeight: number;
    textareaHeight: number;
    controlPadding: [number, number];
    controlFontSize: number;
    controlLineHeight: number;
};

const FORM_INPUT_SIZES: Record<ESize, FormInputSizeTokens> = {
    [ESize.S]: {
        labelFontSize: 13,
        labelMarginBottom: 4,
        paragraphFontSize: 13,
        checkboxRadioSize: 11,
        fieldMarginBottom: 12,
        controlHeight: 35,
        textareaHeight: 65,
        controlPadding: [8, 10],
        controlFontSize: 13,
        controlLineHeight: 15,
    },
    [ESize.M]: {
        labelFontSize: 15,
        labelMarginBottom: 5,
        paragraphFontSize: 15,
        checkboxRadioSize: 13,
        fieldMarginBottom: 15,
        controlHeight: 41,
        textareaHeight: 75,
        controlPadding: [10, 16],
        controlFontSize: 15,
        controlLineHeight: 17,
    },
    [ESize.L]: {
        labelFontSize: 19,
        labelMarginBottom: 8,
        paragraphFontSize: 19,
        checkboxRadioSize: 19,
        fieldMarginBottom: 18,
        controlHeight: 57,
        textareaHeight: 99,
        controlPadding: [16, 22],
        controlFontSize: 19,
        controlLineHeight: 21,
    },
};

export function getFormSizeCssVars(inputSettings: FormInputSettingsLike): FormCssVars {
    const size = FORM_INPUT_SIZES[inputSettings.inputSize ?? ESize.M];

    return {
        '--form-field-label-font-size': pxVar(size.labelFontSize),
        '--form-field-label-margin-bottom': pxVar(size.labelMarginBottom),
        '--form-field-p-font-size': pxVar(size.paragraphFontSize),
        '--form-field-control-size': pxVar(size.checkboxRadioSize),
        '--form-field-margin-bottom': pxVar(size.fieldMarginBottom),
        '--form-control-height': pxVar(size.controlHeight),
        '--form-control-textarea-height': pxVar(size.textareaHeight),
        '--form-control-padding': padding(size.controlPadding),
        '--form-control-font-size': pxVar(size.controlFontSize),
        '--form-control-line-height': pxVar(size.controlLineHeight),
    };
}

// ---------------------------------------------------------------------------
// Submit button (the `buttonStyles` key). Mirrors the button widget's css-vars.
// ---------------------------------------------------------------------------

/**
 * Button-size metrics (driven by `formButtonSettings.size`). `wrapMarginTop` is
 * the vertical wrapper gap; the rest size the horizontal-layout button. Set on
 * `.form-button-wrapper`, inherited by the `.form-button` anchor.
 */
type FormButtonSizeTokens = {
    wrapMarginTop: number;
    height: number;
    buttonPadding: [number, number];
    fontSize: number;
    lineHeight: number;
};

const FORM_BUTTON_SIZES: Record<ESize, FormButtonSizeTokens> = {
    [ESize.S]: { wrapMarginTop: 20, height: 35, buttonPadding: [10, 10], fontSize: 13, lineHeight: 15 },
    [ESize.M]: { wrapMarginTop: 24, height: 41, buttonPadding: [12, 16], fontSize: 15, lineHeight: 17 },
    [ESize.L]: { wrapMarginTop: 28, height: 57, buttonPadding: [18, 22], fontSize: 19, lineHeight: 21 },
};

function getFormButtonSizeCssVars(size: ESize | null | undefined): CssVars {
    const tokens = FORM_BUTTON_SIZES[size ?? ESize.M];

    return {
        '--form-btn-wrap-margin-top': pxVar(tokens.wrapMarginTop),
        '--form-btn-height': pxVar(tokens.height),
        '--form-btn-padding': padding(tokens.buttonPadding),
        '--form-btn-font-size': pxVar(tokens.fontSize),
        '--form-btn-line-height': pxVar(tokens.lineHeight),
    };
}

export function getFormButtonCssVars(
    button: FormButtonSettingsLike,
    formStyleType: FormStyleEnum,
    resolveColor: ColorCssResolver,
    isZero = false
): CssVars {
    const isHorizontal = formStyleType === FormStyleEnum.HORIZONTAL;
    const widthType = (isHorizontal ? button.horizontalFormWidthType : button.widthType) ?? EButtonWidthType.full;

    const vars: CssVars = {
        ...getFormButtonSizeCssVars(button.size),
        '--form-btn-flex-direction': button.buttonIcon?.position === EHorizontalPosition.L ? 'row' : 'row-reverse',
    };

    // Zero-block forms are free-positioned — drop the size-based wrapper gap so the
    // button sits flush with the host's grid. Overrides the size token from above.
    if (isZero) {
        vars['--form-btn-wrap-margin-top'] = '0';
    }

    if (widthType === EButtonWidthType.auto) {
        vars['--form-btn-width'] = 'auto';
    } else if (widthType === EButtonWidthType.fixed) {
        vars['--form-btn-width'] = '100%';
        vars['--form-btn-max-width'] = pxVar(button.widthValue);
    } else {
        vars['--form-btn-width'] = '100%';
    }

    if (isHorizontal) {
        const wrapType = button.horizontalFormWidthType ?? EButtonWidthType.full;

        if (wrapType === EButtonWidthType.full) {
            vars['--form-btn-wrap-width'] = '100%';
        } else if (wrapType === EButtonWidthType.fixed) {
            vars['--form-btn-wrap-width'] = '100%';
            vars['--form-btn-wrap-max-width'] = pxVar(button.widthValue);
        } else {
            vars['--form-btn-wrap-width'] = 'auto';
        }
    }

    if (button.isShadow) {
        const s = button.shadowSettings ?? DEFAULT_SHADOW_SETTINGS;
        vars['--form-btn-shadow'] = shadowValueVar(s);
    }

    if (button.styleType === EButtonStyleType.custom) {
        Object.assign(vars, getThemedButtonCssVars(button, '--form-btn', resolveColor));
    }

    return vars;
}

/** Custom/shadow/hover gates for the submit button (sizing + alignment are variable-driven). */
export function getFormButtonCssClasses(button: FormButtonSettingsLike): string {
    const classes: string[] = [];

    if (button.isShadow) {
        classes.push('form-btn-shadow');
    }

    if (button.styleType === EButtonStyleType.custom) {
        classes.push('form-btn-custom', ...getThemedButtonHoverClasses(button, 'form-btn'));
    }

    return classes.join(' ');
}

// ---------------------------------------------------------------------------
// Chat-bot subscription buttons (the `chatBotWrapper` + `chatBotGroup` keys).
// ---------------------------------------------------------------------------

export function getFormChatBotWrapperCssClasses(
    settings: FormButtonSettingsLike & ChatBotsButtonSettingsLike,
    inputSettings: FormInputSettingsLike
): string {
    const layout =
        inputSettings.formStyleType === FormStyleEnum.HORIZONTAL ? 'form-chatbot-horizontal' : 'form-chatbot-vertical';
    const mode = settings.type === EButtonType.icon ? 'form-chatbot-icon' : 'form-chatbot-button';

    return `${layout} ${mode}`;
}

/**
 * Chat-bot icon-mode metrics (driven by `chatBotsButtonSettings.buttonSize`).
 * Only the `.form-chatbot-icon` LESS rules consume these; harmless in button
 * mode. Set on `.form-chatbot-wrapper`, inherited by the `.form-chatbot-anchor`.
 */
type FormChatBotIconSizeTokens = {
    anchorPadding: [number, number];
    gap: number;
    iconSize: number;
    fontSize: number;
    lineHeight: number;
};

const FORM_CHATBOT_ICON_SIZES: Record<ESize, FormChatBotIconSizeTokens> = {
    [ESize.S]: { anchorPadding: [8, 12], gap: 2, iconSize: 18, fontSize: 10, lineHeight: 14 },
    [ESize.M]: { anchorPadding: [12, 16], gap: 4, iconSize: 24, fontSize: 12, lineHeight: 17 },
    [ESize.L]: { anchorPadding: [12, 16], gap: 6, iconSize: 32, fontSize: 14, lineHeight: 20 },
};

function getFormChatBotIconSizeCssVars(size: ESize | null | undefined): CssVars {
    const tokens = FORM_CHATBOT_ICON_SIZES[size ?? ESize.M];

    return {
        '--form-chatbot-icon-padding': padding(tokens.anchorPadding),
        '--form-chatbot-icon-gap': pxVar(tokens.gap),
        '--form-chatbot-icon-size': pxVar(tokens.iconSize),
        '--form-chatbot-icon-font-size': pxVar(tokens.fontSize),
        '--form-chatbot-icon-line-height': pxVar(tokens.lineHeight),
    };
}

export function getFormChatBotCssVars(
    settings: FormButtonSettingsLike & ChatBotsButtonSettingsLike,
    resolveColor: ColorCssResolver
): FormCssVars {
    const vars: FormCssVars = getFormChatBotIconSizeCssVars(settings.buttonSize);

    if (settings.styleType === EButtonStyleType.custom) {
        Object.assign(vars, getThemedButtonCssVars(settings, '--form-chatbot', resolveColor));
    }

    return vars;
}

export function getFormChatBotCssClasses(settings: FormButtonSettingsLike & ChatBotsButtonSettingsLike): string {
    if (settings.styleType !== EButtonStyleType.custom) {
        return '';
    }

    return ['form-chatbot-custom', ...getThemedButtonHoverClasses(settings, 'form-chatbot')].join(' ');
}

// ---------------------------------------------------------------------------
// Modal twins (the `modal*` keys — modal/two-step form view). Driven by the
// `inputSettings.modal*` fields + `modalFormButtonSettings`.
// ---------------------------------------------------------------------------

export function getFormModalBodyCssVars(
    inputSettings: FormInputSettingsLike,
    resolveColor: ColorCssResolver
): FormCssVars {
    return {
        '--form-modal-body-bg': resolveColor(inputSettings.modalBackgroundColor ?? '#fbfcfc'),
        '--form-modal-body-radius': pxVar(inputSettings.modalBorderRadius ?? 6),
        '--form-modal-padding': pxVar(inputSettings.modalPadding ?? 45),
        '--form-modal-title-color': resolveColor(inputSettings.modalTitleTextColor),
    };
}

export function getFormModalInputCssVars(
    inputSettings: FormInputSettingsLike,
    resolveColor: ColorCssResolver
): FormCssVars {
    return {
        '--form-modal-text-color': resolveColor(inputSettings.modalTextColor),
        '--form-modal-input-bg': resolveColor(inputSettings.modalInputBackgroundColor),
        '--form-modal-input-border-color': resolveColor(inputSettings.modalInputBorderColor),
        '--form-modal-input-text-color': resolveColor(inputSettings.modalInputTextColor),
        '--form-modal-input-radius': pxVar(inputSettings.modalInputBorderRadius ?? 0),
    };
}

/** Full/bottom border gate for the modal fields/controls (sizing is variable-driven). */
export function getFormModalInputCssClasses(inputSettings: FormInputSettingsLike): string {
    return inputSettings.modalInputBorderType === InputBorderEnum.BOTTOM_BORDER
        ? 'form-modal-input-bottom-border'
        : 'form-modal-input-full-border';
}

/**
 * Modal input-size metrics (driven by `inputSettings.modalInputSize`). Reuses the
 * inline-form `FORM_INPUT_SIZES` token table under the `--form-modal-*` namespace.
 * Set on `.form-modal-container`, inherited by the field/control descendants.
 */
export function getFormModalSizeCssVars(inputSettings: FormInputSettingsLike): FormCssVars {
    const size = FORM_INPUT_SIZES[inputSettings.modalInputSize ?? ESize.M];

    return {
        '--form-modal-field-margin-bottom': pxVar(size.fieldMarginBottom),
        '--form-modal-field-label-font-size': pxVar(size.labelFontSize),
        '--form-modal-field-label-margin-bottom': pxVar(size.labelMarginBottom),
        '--form-modal-field-p-font-size': pxVar(size.paragraphFontSize),
        '--form-modal-field-control-size': pxVar(size.checkboxRadioSize),
        '--form-modal-control-height': pxVar(size.controlHeight),
        '--form-modal-control-textarea-height': pxVar(size.textareaHeight),
        '--form-modal-control-padding': padding(size.controlPadding),
        '--form-modal-control-font-size': pxVar(size.controlFontSize),
        '--form-modal-control-line-height': pxVar(size.controlLineHeight),
    };
}

export function getFormModalButtonCssVars(button: FormButtonSettingsLike, resolveColor: ColorCssResolver): FormCssVars {
    const vars: FormCssVars = {
        '--form-modal-btn-wrap-margin-top': pxVar(FORM_BUTTON_SIZES[button?.size ?? ESize.M].wrapMarginTop),
    };

    if (button?.isShadow) {
        const s = button.shadowSettings ?? DEFAULT_SHADOW_SETTINGS;
        vars['--form-modal-btn-shadow'] = shadowValueVar(s);
    }

    if (button?.styleType === EButtonStyleType.custom) {
        Object.assign(vars, getThemedButtonCssVars(button, '--form-modal-btn', resolveColor));
    }

    return vars;
}

export function getFormModalButtonCssClasses(button: FormButtonSettingsLike): string {
    const classes: string[] = [];

    if (button?.isShadow) {
        classes.push('form-modal-btn-shadow');
    }

    if (button?.styleType === EButtonStyleType.custom) {
        classes.push('form-modal-btn-custom', ...getThemedButtonHoverClasses(button, 'form-modal-btn'));
    }

    return classes.join(' ');
}
